import { useApolloClient } from '@apollo/react-hooks';
import { IDropdownOption } from '@fluentui/react';
import ToggleGroup from 'components/controls/ToggleGroup';
import { InputDialog } from 'components/layout/Dialog';
import { useEffect, useState } from 'react';
import { ObservationClosureReasonEnum } from 'shared/enums/ObservationClosureReason.enum';
import { StyledControlContainer } from 'shared/styled/control.styled';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { IClosureReason, IObservationCompletionLookupData } from 'shared/queries/observation-completion.queries.types';
import { ErrorListToastMessage } from 'modules/errors';
import { ActionResponse } from 'modules/errors/classes/ActionResponse';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { useToasts } from 'react-toast-notifications';
import { usePermissions, UserPermission, WriteOn } from 'modules/permissions';
import DateTimeInput from 'components/controls/DateTimeInput';
import { ConvertToUTC } from 'shared/utils/date.utils';
import { AmmendOpportunityLoadingState } from 'sections/opportunities-management/pages/ViewAmmendOpportunity/enums/ammend-opportunity-loading-state.enum';
import { CompleteOpportunityResponse, CompleteOpportunityVariables } from 'sections/opportunities-management/pages/ViewAmmendOpportunity/mutations/ammend-opportunity.mutations.types';
import { CompleteOpportunity } from 'sections/opportunities-management/pages/ViewAmmendOpportunity/mutations/ammend-opportunity.mutations';
import { GetOpportunityCompletionLookups } from 'sections/opportunities-management/pages/ViewAmmendOpportunity/queries/opportunity-completion.queries';
import { Opportunity } from 'sections/opportunities-management/pages/ViewAmmendOpportunity/queries/view-opportunity.queries.types';

interface CompleteDialogProps {
   opportunity: Opportunity | undefined;
   isOpen: boolean;
   setLoadingState: (loadingMessage: AmmendOpportunityLoadingState, isLoading: boolean) => void;
   onDismiss: () => void;
   onComplete: () => void;
}

export const CompleteDialog = ({
   opportunity,
   isOpen,
   setLoadingState,
   onDismiss,
   onComplete
}: CompleteDialogProps) => {
   const client = useApolloClient();
   const { addToast } = useToasts();
   const { hasPermission } = usePermissions();

   const [selectedClosureReason, setSelectedClosureReason] = useState<ObservationClosureReasonEnum>();
   const [closureReasonValidationMessage, setClosureReasonValidationMessage] = useState<string>();
   const [selectedClosureDate, setSelectedClosureDate] = useState<string | null>();
   const [closureDateValidationMessage, setClosureDateValidationMessage] = useState<string>();

   const [loadingClosureReasons, setLoadingClosureReasons] = useState<boolean>(true);
   const [closureReasons, setClosureReasons] = useState<IClosureReason[]>();

   useEffect(() => {
      loadReporterTypes();
      setSelectedClosureDate(new Date().toISOString());
   }, []);

   const loadReporterTypes = async () => {
      setLoadingClosureReasons(true);

      try {
         let result = await client.query<IObservationCompletionLookupData>({
            query: GetOpportunityCompletionLookups,
            variables: {
               request: { type: 'OPPORTUNITYCLOSUREREASON', selectedLanguage: 'GB' }
            },
            fetchPolicy: 'network-only'
         });

         if (result?.data?.closureReasons != null) {
            setClosureReasons(result.data.closureReasons);
         }
      } finally {
         setLoadingClosureReasons(false);
      }
   };

   const resetFormAndClose = () => {
      setSelectedClosureReason(undefined);
      setClosureReasonValidationMessage(undefined);

      onDismiss();
   };

   const updateClosureReason = (selection: string | number) => {
      setClosureReasonValidationMessage(undefined);
      setSelectedClosureReason(selection as ObservationClosureReasonEnum);
   };

   const updateClosureDate = (date: string | null | undefined) => {
      setClosureDateValidationMessage(undefined);
      setSelectedClosureDate(date);
   };

   const validateClosureReason = (): boolean => {
      if (!selectedClosureReason) {
         setClosureReasonValidationMessage('Please select a closure reason');
         return false;
      }

      setClosureReasonValidationMessage(undefined);
      return true;
   };

   const validateClosureDate = (value: string | undefined): boolean => {
      if (!value) {
         setClosureDateValidationMessage('You must provide a closure date');
         return false;
      } else if (value && new Date(value).getTime() > Date.now()) {
         setClosureDateValidationMessage('Closure date cannot be in the future');
         return false;
      } else if (
         opportunity?.incidentDate! &&
         value &&
         new Date(value).getTime() < Date.parse(opportunity?.incidentDate.toString())
      ) {
         setClosureDateValidationMessage('Closure date cannot be before the Incident Date');
         return false;
      }

      setClosureDateValidationMessage(undefined);
      return true;
   };

   const submitCompletion = () => {
      var closureReasonValid = validateClosureReason();
      var closeDateValid = validateClosureDate(selectedClosureDate ?? undefined);

      if (!closureReasonValid || !closeDateValid) {
         return;
      }

      handleComplete();
   };

   const handleComplete = async (): Promise<ActionResponse> => {
      setLoadingState(AmmendOpportunityLoadingState.savingChanges, true);
      const actionResponse = new ActionResponse();

      if (opportunity?.observationStatus !== ObservationStatusEnum.Open) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You are attempting to amend a closed Opportunity which is prohibited'
            }
         });
      }

      if (!hasPermission(WriteOn(UserPermission.Completion))) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You do not have the required permissions to perform this action'
            }
         });
      }

      try {
         await client.mutate<CompleteOpportunityResponse, CompleteOpportunityVariables>({
            mutation: CompleteOpportunity,
            variables: {
               request: {
                  opportunityId: opportunity?.observationId ?? 0,
                  closeDate: ConvertToUTC(selectedClosureDate!),
                  closureReason: selectedClosureReason!.toUpperCase()
               }
            }
         });

         actionResponse.set({ successful: true });
      } catch (error: any) {
         actionResponse.set({
            successful: false,
            error: {
               message: 'There was an issue closing the Opportunity',
               error: error
            }
         });
      }

      const response = actionResponse;

      setLoadingState(AmmendOpportunityLoadingState.savingChanges, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={response.getErrorMessages()} />, { appearance: 'error' });
      } else {
         addToast('Changes saved successfully', { appearance: 'success' });

         onComplete();
      }

      return response;
   };

   return (
      <InputDialog
         title={"Complete Opportunity"}
         subText={"Please fill in the supporting completion details"}
         show={isOpen}
         onCancel={resetFormAndClose}
         onDone={submitCompletion}
         width={'500px'}
         cancelButtonText={'Cancel'}
         doneButtonText={'Done'}
      >
         <StyledControlContainer>
            <DateTimeInput
               label={'Date Closed'}
               value={selectedClosureDate}
               onDateTimeSelect={updateClosureDate}
               errorMessage={closureDateValidationMessage}
            />
         </StyledControlContainer>
         <StyledControlContainer>
            <ToggleGroup
               label={'Reason for Closure'}
               options={mapToDropdownOptions<IClosureReason, IDropdownOption>(
                  closureReasons,
                  type => type.name,
                  type => `${type.name.toProperCase()}`
               )}
               selectedKey={selectedClosureReason}
               onChange={updateClosureReason}
               errorMessage={closureReasonValidationMessage}
               isLoading={loadingClosureReasons}
            />
         </StyledControlContainer>
      </InputDialog>
   );
};
